import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import '../App.css';
import client from '../api/client';
import { toast } from 'react-toastify';

function HospitalRegister() {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    hospitalName: '',
    contactPersonName: '',
    contactPersonRole: '',
    address: '',
    pincode: '',
    mobileNumber: '',
    emailId: '',
    hospitalWebsite: '',
    landlineNumber: '',
    password: '',
    reenterPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showReenterPassword, setShowReenterPassword] = useState(false);
  const [pincodeError, setPincodeError] = useState('');
  const [contactError, setContactError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [locationName, setLocationName] = useState('');
  const [idDistrict, setIdDistrict] = useState(null);
  const [isValidatingPincode, setIsValidatingPincode] = useState(false);
  const [isValidatingContact, setIsValidatingContact] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Validation for pincode (only numbers, max 6 digits)
    if (name === 'pincode') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 6);
      setFormData(prev => ({ ...prev, [name]: numericValue }));
      setPincodeError('');
      setLocationName('');
      setIdDistrict(null);
      return;
    }

    // Validation for mobile number (only numbers, max 10 digits)
    if (name === 'mobileNumber') {
      const numericValue = value.replace(/[^0-9]/g, '').slice(0, 10);
      setFormData(prev => ({ ...prev, [name]: numericValue }));
      setContactError('');
      setMobileError('');
      return;
    }
    if (name === 'password' || name === 'reenterPassword') {
      if (value.length > 20) {
        toast.error('Password cannot exceed 20 characters!');
        return; // stops user from typing more
      }
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }

    if (name === 'emailId') {
      setFormData(prev => ({ ...prev, [name]: value }));
      setContactError('');
      setEmailError('');
      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Character counter
  const addressCount = formData.address.length;

  // Password validation
  const passwordsMatch = formData.password === formData.reenterPassword && formData.reenterPassword !== '';

  // Validate pincode via API
  const validatePincode = async () => {
    let message = '';
    setPincodeError('');
    setLocationName('');
    setIdDistrict(null);

    if (formData.pincode.length !== 6) {
      message = 'Enter a 6-digit pincode.';
      setPincodeError(message);
      return { ok: false, message };
    }

    setIsValidatingPincode(true);
    try {
      const response = await client.get(`/api/districts/validate-pincode?pincode=${formData.pincode}`);
      const result = response.data?.data;

      if (result?.districtName) {
        setLocationName(result.districtName);
        setIdDistrict(result.idDistrict ?? null);
        return { ok: true, message: '' };
      }

      message = 'Enter a valid pincode.';
      setPincodeError(message);
      return { ok: false, message };
    } catch (error) {
      message = 'Unable to validate pincode. Please try again.';
      setPincodeError(message);
      return { ok: false, message };
    } finally {
      setIsValidatingPincode(false);
    }
  };

  // Validate contact via API (field can be 'mobile', 'email', or 'both')
  const validateContact = async (field = 'both') => {
    let message = '';
    if (field === 'both') {
      setContactError('');
    }
    if (field !== 'email') {
      setMobileError('');
    }
    if (field !== 'mobile') {
      setEmailError('');
    }

    const emailValid = field === 'mobile' ? true : /\S+@\S+\.\S+/.test(formData.emailId);
    const mobileValid = field === 'email' ? true : formData.mobileNumber.length === 10;

    if (!emailValid || !mobileValid) {
      if (!emailValid) {
        setEmailError('Enter a valid email.');
        message = 'Enter a valid email.';
      }
      if (!mobileValid) {
        setMobileError('Enter a valid 10-digit mobile number.');
        message = message || 'Enter a valid 10-digit mobile number.';
      }
      if (field === 'both' && message) {
        setContactError(message);
      }
      return { ok: false, message: message || 'Enter valid contact details.' };
    }

    setIsValidatingContact(true);
    try {
      const payload = {
        emailID: field === 'mobile' ? null : formData.emailId || null,
        mobileNumber: field === 'email' ? null : formData.mobileNumber || null
      };
      const response = await client.post('/api/users/validate-contact', payload);
      const exists = response.data?.data;

      if (exists) {
        if (field === 'email') {
          message = 'Email already exists. Enter a different one.';
          setEmailError(message);
        } else if (field === 'mobile') {
          message = 'Mobile number already exists. Enter a different one.';
          setMobileError(message);
        } else {
          message = 'Email or mobile already exists. Enter a different one.';
          setContactError(message);
        }
        return { ok: false, message };
      }

      return { ok: true, message: '' };
    } catch (error) {
      message = 'Unable to validate contact. Please try again.';
      if (field === 'email') {
        setEmailError(message);
      } else if (field === 'mobile') {
        setMobileError(message);
      } else {
        setContactError(message);
      }
      return { ok: false, message };
    } finally {
      setIsValidatingContact(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    const [pincodeResult, mobileResult, emailResult] = await Promise.all([
      validatePincode(),
      validateContact('mobile'),
      validateContact('email')
    ]);

    if (!pincodeResult.ok) {
      toast.error(pincodeResult.message || 'Please enter a valid pincode.');
      return;
    }
    if (!mobileResult.ok) {
      toast.error(mobileResult.message || 'Please enter a valid mobile number.');
      return;
    }
    if (!emailResult.ok) {
      toast.error(emailResult.message || 'Please enter a valid email ID.');
      return;
    }

    // Validate password match
    if (formData.password !== formData.reenterPassword) {
      toast.error('Passwords do not match!');
      return;
    }

    // Validate password length
    if (formData.password.length < 6 || formData.password.length > 20) {
      toast.error('Password must be between 6 and 20 characters!');
      return;
    }

    // Validate address length
    if (formData.address.trim().length === 0) {
      toast.error('Address is required!');
      return;
    }

    if (!locationName) {
      toast.error('Enter a valid pincode to fetch location.');
      return;
    }

    const payload = {
      EmailID: formData.emailId,
      MobileNumber: formData.mobileNumber,
      Password: formData.password,
      HospitalName: formData.hospitalName,
      Address: formData.address,
      IdDistrict: idDistrict ?? 0,
      Pincode: formData.pincode,
      ContactPersonName: formData.contactPersonName,
        ContactPersonRole: formData.contactPersonRole || null,
      LandlineNumber: formData.landlineNumber || null,
      Website: formData.hospitalWebsite || null,
      IsSuspended: false
    };

    try {
      setIsSubmitting(true);
      await client.post('/api/hospitals', payload);
      toast.success('Registration successful!');
      navigate('/');
    } catch (error) {
      toast.error('Registration failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All entered data will be lost.')) {
      setFormData({
        hospitalName: '',
        contactPersonName: '',
          contactPersonRole: '',
        address: '',
        pincode: '',
        mobileNumber: '',
        emailId: '',
        hospitalWebsite: '',
          landlineNumber: '',
          password: '',
          reenterPassword: ''
      });
   
      navigate('/');
    }
  };

  return (
    <div className="App">
      <Header />

      <section className="login-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-12">
              <div className="login-card">
                <div className="login-header text-center mb-4">
                  <h2 className="login-title">Hospital Registration</h2>
                  <p className="login-subtitle">
                    Please fill in all the required details to complete your registration.
                  </p>
                </div>

                <form className="login-form" onSubmit={handleSubmit}>
                  <div className="row">
                    {/* Name of Hospital */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="hospitalName" className="form-label">
                        Name of Hospital <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="hospitalName"
                        name="hospitalName"
                        value={formData.hospitalName}
                        onChange={handleChange}
                        placeholder="Enter hospital name"
                        maxLength="60"
                        required
                      />
                    </div>

                    {/* Contact Person Name */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="contactPersonName" className="form-label">
                        Contact Person Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="contactPersonName"
                        name="contactPersonName"
                        value={formData.contactPersonName}
                        onChange={handleChange}
                        placeholder="Enter contact person name"
                        maxLength="60"
                        required
                      />
                    </div>

                    {/* Contact Person Role */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="contactPersonRole" className="form-label">
                        Contact Person Role
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="contactPersonRole"
                        name="contactPersonRole"
                        value={formData.contactPersonRole}
                        onChange={handleChange}
                        placeholder="Enter contact person role"
                        maxLength="100"
                      />
                    </div>

                    {/* Address */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="address" className="form-label">
                        Address <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        id="address"
                        name="address"
                        rows="3"
                        value={formData.address}
                        onChange={handleChange}
                        placeholder="Enter hospital address"
                        maxLength="200"
                        required
                      ></textarea>
                      <small className="form-text text-muted">
                        <span id="addressCount">{addressCount}</span>/200 characters
                      </small>
                    </div>

                    {/* Pincode */}
                    
                      <div className="col-md-6 p-2">
                        <label htmlFor="pincode" className="form-label">
                          Pincode <span className="text-danger">*</span>
                        </label>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control w-25"
                            id="pincode"
                            name="pincode"
                            value={formData.pincode}
                          onChange={handleChange}
                          onBlur={validatePincode}
                            placeholder="Enter 6-digit pincode"
                            maxLength="6"
                            pattern="[0-9]{6}"
                            required
                          />

                          <input
                            type="text"
                            className="form-control w-75 form-control-display-only"
                            id="location"
                            name="location"
                            value={locationName}
                            placeholder="Location"
                            readOnly
                            tabIndex={-1}
                            aria-readonly="true"
                          />

                        </div>
                      {pincodeError && (
                        <small className="form-text text-danger">{pincodeError}</small>
                      )}
                      {isValidatingPincode && (
                        <small className="form-text text-muted">Validating pincode...</small>
                      )}

                      </div>


                    {/* Mobile Number */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="mobileNumber" className="form-label">
                        Mobile Number <span className="text-danger">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        id="mobileNumber"
                        name="mobileNumber"
                        value={formData.mobileNumber}
                          onChange={handleChange}
                          onBlur={() => validateContact('mobile')}
                        placeholder="Enter 10-digit mobile number"
                        maxLength="10"
                        pattern="[0-9]{10}"
                        required
                      />
                      {mobileError && (
                        <small className="form-text text-danger">{mobileError}</small>
                      )}
                    </div>
                    {/* Landline Number */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="mobileNumber" className="form-label">
                      Landline Number
                      </label>
                      <input
                        type="tel"
                        className="form-control"
                        id="landlineNumber"
                        name="landlineNumber"
                        value={formData.landlineNumber}
                          onChange={handleChange}
                          placeholder="Enter landline number"
                          maxLength="15"
                          pattern="[0-9]{6,15}"
                      />
                    </div>

                    {/* Email ID */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="emailId" className="form-label">
                        Email ID <span className="text-danger">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        id="emailId"
                        name="emailId"
                        value={formData.emailId}
                          onChange={handleChange}
                          onBlur={() => validateContact('email')}
                        placeholder="Enter email ID"
                        maxLength="50"
                        required
                      />
                      {emailError && (
                        <small className="form-text text-danger">{emailError}</small>
                      )}
                      {isValidatingContact && (
                        <small className="form-text text-muted">Validating contact...</small>
                      )}
                    </div>

                    {/* Hospital Website */}
                    <div className="col-md-12 p-2">
                      <label htmlFor="hospitalWebsite" className="form-label">
                        Hospital Website
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        id="hospitalWebsite"
                        name="hospitalWebsite"
                        value={formData.hospitalWebsite}
                        onChange={handleChange}
                        placeholder="Enter hospital website URL"
                        maxLength="60"
                      />
                    </div>

                    {/* Password */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="password" className="form-label">
                        Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          className="form-control"
                          id="password"
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          placeholder="Enter password (6-20 characters)"
                          minLength="6"                          
                          required
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      <small className="form-text text-muted">Password must be 6-20 characters long</small>
                    </div>

                    {/* Re-enter Password */}
                    <div className="col-md-6 p-2">
                      <label htmlFor="reenterPassword" className="form-label">
                        Re-enter Password <span className="text-danger">*</span>
                      </label>
                      <div className="input-group">
                        <input
                          type={showReenterPassword ? 'text' : 'password'}
                          className={`form-control ${formData.reenterPassword && !passwordsMatch ? 'is-invalid' : formData.reenterPassword && passwordsMatch ? 'is-valid' : ''}`}
                          id="reenterPassword"
                          name="reenterPassword"
                          value={formData.reenterPassword}
                          onChange={handleChange}
                          placeholder="Re-enter password"
                          minLength="6"
                          maxLength="20"
                          required
                        />
                        <button
                          className="btn btn-outline-secondary"
                          type="button"
                          onClick={() => setShowReenterPassword(!showReenterPassword)}
                        >
                          {showReenterPassword ? <FaEyeSlash /> : <FaEye />}
                        </button>
                      </div>
                      {formData.reenterPassword && !passwordsMatch && (
                        <div className="invalid-feedback d-block">Passwords do not match</div>
                      )}
                    </div>

                    {/* Submit Button */}
                    <div className="text-center mt-4">
                      <button type="submit" className="btn login-submit-btn px-5 me-2">
                        {isSubmitting ? 'Submitting...' : 'Register'}
                      </button>
                      <button
                        type="button"
                        className="btn cancel-btn px-5"
                        onClick={handleCancel}
                      >
                        Cancel
                      </button>
                    </div>

                   
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default HospitalRegister;

